// ── resources-helpers: module access + category taxonomy + manage/download ──
// Wave 8 (Resources). Shared by the Resources list / detail / manage surfaces.
// Ids are STRINGS (bigint-safe) — passed VERBATIM, never Number()'d.
//
// ── PARTICIPANT EXCLUSION (Wave 8 release gate) ──────────────────────────────
// A `participant` is DENIED the ADMIN Resources module across EVERY surface —
// route, nav, list, detail, manage, upload/download. The participant's own
// resources context is the Wave-9 dashboard, NOT this admin surface. This is a
// fail-closed allow-list: an unknown/new role is denied until explicitly added.
// Defense-in-depth at BOTH layers — the route manifest denies participant at the
// route (resourceModuleRoles → generic-not-found + no nav), AND every resources
// page fail-closes via canAccessResourcesModule (load-bearing: the resources read
// NEVER fires for a participant).

import { humanizeLabel } from "@vjal/ui";

import type { CoachingRole } from "../../domain/roles";
import type { CoachingResourceDto } from "../../services/coaching-dtos";

// ── Resources MODULE access (fail-closed allow-list) ─────────────────────────
// MUST equal the route manifest's resourceModuleRoles. The biting tests assert
// that participant is NOT here and that the page read never fires for participant.
export const RESOURCES_MODULE_ROLES: readonly CoachingRole[] = [
  "super_admin",
  "admin",
  "coach",
  "company_admin",
  "cohort_lead"
];

/**
 * Whether the role may load the ADMIN Resources module (fail-closed). A
 * participant is ALWAYS false; the resources read is gated on this so no
 * resource request is made for an excluded role.
 */
export function canAccessResourcesModule(role: CoachingRole): boolean {
  return RESOURCES_MODULE_ROLES.includes(role);
}

// ── Resource manage (create/edit/archive/attach) — STAFF only ────────────────
// Per the backend contract (createResource/updateResource require
// manage.resources), only super_admin/admin manage resources. Coach /
// company_admin / cohort_lead are scoped VIEW. The biting test asserts ABSENCE
// of every manage affordance for the non-staff roles.
export function canManageResources(role: CoachingRole): boolean {
  return role === "super_admin" || role === "admin";
}

// ── Resource CATEGORY taxonomy (FE buckets over the free-form resource_type) ─
// The backend `resource_type` is a free-form string (no DB enum). The FE buckets
// it into the three product categories WITHOUT inventing a value: an unknown
// type falls into `program_resource` (the neutral default), never fabricated.
export type ResourceCategory = "reference_videos" | "documentation" | "program_resource";

export const RESOURCE_CATEGORIES: readonly ResourceCategory[] = [
  "reference_videos",
  "documentation",
  "program_resource"
];

export const resourceCategoryLabels: Record<ResourceCategory, string> = {
  reference_videos: "Reference videos",
  documentation: "Documentation",
  program_resource: "Program resources"
};

// Map a raw backend resource_type to a display category (no fabrication: the
// neutral default is program_resource). Video-ish types → reference_videos;
// doc-ish types → documentation.
const VIDEO_TYPES = new Set(["reference_video", "reference_videos", "video", "recording", "reference-video"]);
const DOC_TYPES = new Set(["documentation", "document", "doc", "pdf", "guide", "handbook", "checklist"]);

export function resourceCategory(resourceType: string | null | undefined): ResourceCategory {
  const normalized = (resourceType ?? "").trim().toLowerCase().replace(/\s+/g, "_");
  if (VIDEO_TYPES.has(normalized)) return "reference_videos";
  if (DOC_TYPES.has(normalized)) return "documentation";
  return "program_resource";
}

export function resourceCategoryOf(resource: CoachingResourceDto): ResourceCategory {
  return resourceCategory(resource.resource_type);
}

/** Whether a resource is a reference video (the no-download-for-coach tier). */
export function isReferenceVideo(resource: CoachingResourceDto): boolean {
  return resourceCategoryOf(resource) === "reference_videos";
}

// ── REFERENCE-VIDEO DOWNLOAD restriction (Wave 8 contract check) ─────────────
// Coach may VIEW reference videos but may NOT download them. STAFF may download
// any category. company_admin / cohort_lead get the same view-only posture as
// coach on reference videos (scoped read; download follows the documentation /
// program_resource categories where allowed). This is the FE half of the
// view≥download discipline; the backend upload/download endpoints are 503
// (gate-closed) anyway, so a download is NEVER actually performed — but the
// AFFORDANCE itself is hidden for the restricted role/category so the UI never
// even offers a reference-video download to a coach.
export function canRoleDownloadResource(role: CoachingRole, resource: CoachingResourceDto): boolean {
  if (role === "super_admin" || role === "admin") return true;
  // Non-staff (coach / company_admin / cohort_lead): reference videos are
  // VIEW-only — the download affordance is never offered.
  if (isReferenceVideo(resource)) return false;
  // documentation / program_resource: download where allowed (the actual
  // transfer is still the 503 gate-closed honest state).
  return true;
}

// ── A non-empty, non-"new" route segment is the only thing that's a real id. ──
export function isValidResourceId(id: string | null | undefined): id is string {
  return typeof id === "string" && id.trim().length > 0 && id !== "new";
}

/** Neutral display for a missing optional field — never a fabricated value. */
export function orDash(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : "—";
}

/** Humanized resource-type label (falls back to the category label). */
export function resourceTypeLabel(resource: CoachingResourceDto): string {
  const raw = (resource.resource_type ?? "").trim();
  if (raw.length > 0) return humanizeLabel(raw);
  return resourceCategoryLabels[resourceCategoryOf(resource)];
}

export function resourceStatusLabel(status: string | null | undefined): string {
  return humanizeLabel(status ?? "draft");
}

export function resourceScopeLabel(resource: CoachingResourceDto): string {
  if (resource.is_public) return "Global";
  const cohort = (resource.cohort_name ?? "").trim();
  if (cohort.length > 0) return `Cohort: ${cohort}`;
  const company = (resource.company_name ?? "").trim();
  if (company.length > 0) return `Company: ${company}`;
  return "Scoped";
}
